/* eslint-disable indent */
import { useState } from 'react'
import Form from 'react-bootstrap/Form'
import Dropdown from 'react-bootstrap/Dropdown'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import axios from 'axios'
import { getTranslation } from '../services/translations'

const StaffManagement = ({
  user,
  staffData,
  fetchTeamStaffMembers,
  handleGrantStaffRole,
  handleRevokeStaffRole,
  teamStaffMembers,
  loadingStaff,
  lang = 'fi',
}) => {
  const t = key => getTranslation(lang, key)

  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedRole, setSelectedRole] = useState('')
  const [selectedNationalTeam, setSelectedNationalTeam] = useState(null)

  // Tarkista onko käyttäjä HeadScout jossakin maassa
  const isHeadScout = () => {
    return (
      (staffData &&
        staffData.staff &&
        staffData.staff.some(s => s.role === 'HeadScout')) ||
      false
    )
  }

  // Hae HeadScout-maat
  const getHeadScoutTeams = () => {
    return (
      (staffData &&
        staffData.staff &&
        staffData.staff.filter(s => s.role === 'HeadScout')) ||
      []
    )
  }

  // Näytä vain HeadScouteille
  if (!isHeadScout()) {
    return null
  }

  const handleSearch = async event => {
    const query = event.target.value
    setSearchTerm(query)

    if (query.length >= 3) {
      try {
        const response = await axios.get(`/users/list/${query}`)
        setSearchResults(response.data)
      } catch (error) {
        console.error('Error fetching users:', error)
        setSearchResults([])
      }
    } else {
      setSearchResults([])
    }
  }

  const handleUserSelect = u => {
    setSelectedUser(u)
    setSearchTerm(u.LoginName)
    setSearchResults([])
  }

  const resetSelections = () => {
    setSelectedUser(null)
    setSelectedRole('')
    setSearchTerm('')
    setSelectedNationalTeam(null)
  }

  const onGrantRole = async () => {
    await handleGrantStaffRole(selectedUser, selectedRole, selectedNationalTeam)
    resetSelections()
  }

  const activeTeamName =
    (staffData &&
      staffData.staff &&
      (
        staffData.staff.find(
          s => s.nationalTeamId === staffData.activeNationalTeam
        ) || {}
      ).nationalTeamName) ||
    ''

  return (
    <div>
      {/* Käyttäjien hallinta */}
      <div style={{ marginBottom: '20px' }}>
        <h5>{t('userManagement')}</h5>
        <Form.Control
          type="text"
          placeholder={t('searchUser')}
          value={searchTerm}
          onChange={handleSearch}
          autoComplete="off"
          style={{
            backgroundColor: '#23272A',
            color: '#FFFFFF',
            marginBottom: '10px',
            width: '25ch',
          }}
        />
        {searchResults.length > 0 && (
          <Dropdown>
            <Dropdown.Menu show>
              {searchResults.map(u => (
                <Dropdown.Item key={u.id} onClick={() => handleUserSelect(u)}>
                  {u.LoginName}
                  {u.Staff && u.Staff.length > 0 && (
                    <small className="ms-2 text-muted">
                      ({u.Staff.map(s => s.nationalTeamName).join(', ')})
                    </small>
                  )}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}
        {selectedUser && (
          <div className="mt-2">
            <p>
              <strong>{t('selected')}:</strong> {selectedUser.LoginName}
            </p>
            {selectedUser.Staff && selectedUser.Staff.length > 0 && (
              <p>
                <strong>{t('currentRoles')}:</strong>{' '}
                {selectedUser.Staff.map(
                  s => `${s.nationalTeamName}: ${s.role}`
                ).join(', ')}
              </p>
            )}

            <Dropdown className="mb-2">
              <Dropdown.Toggle variant="secondary" size="sm">
                {(selectedNationalTeam &&
                  selectedNationalTeam.nationalTeamName) ||
                  t('selectCountry')}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {getHeadScoutTeams().map(team => (
                  <Dropdown.Item
                    key={team.nationalTeamId}
                    onClick={() => setSelectedNationalTeam(team)}
                  >
                    {team.nationalTeamName}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown className="mb-2">
              <Dropdown.Toggle variant="secondary" size="sm">
                {selectedRole || t('selectRole')}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSelectedRole('Scout')}>
                  Scout
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSelectedRole('HeadScout')}>
                  HeadScout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {selectedUser && selectedRole && selectedNationalTeam && (
              <Button variant="success" size="sm" onClick={onGrantRole}>
                {t('grantRole')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Staff-jäsenten lista */}
      {staffData && staffData.activeNationalTeam && (
        <div style={{ marginBottom: '20px' }}>
          <h5>
            {activeTeamName} - {t('staff')}
            <Button
              variant="link"
              size="sm"
              onClick={() =>
                fetchTeamStaffMembers(staffData.activeNationalTeam)
              }
            >
              {t('refresh')}
            </Button>
          </h5>
          {loadingStaff ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Table size="sm" variant="dark" striped>
              <thead>
                <tr>
                  <th>{t('user')}</th>
                  <th>{t('role')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {teamStaffMembers.map(member => (
                  <tr key={member.oauthToken}>
                    <td>{member.loginName}</td>
                    <td>
                      <Badge
                        bg={
                          (member.staffRole && member.staffRole.role) ===
                          'HeadScout'
                            ? 'success'
                            : 'secondary'
                        }
                      >
                        {member.staffRole && member.staffRole.role}
                      </Badge>
                    </td>
                    <td>
                      {member.userId !== user.id && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            handleRevokeStaffRole(
                              member.oauthToken,
                              staffData.activeNationalTeam
                            )
                          }
                        >
                          {t('remove')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {teamStaffMembers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center">
                      {t('noStaffMembers')}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      )}
    </div>
  )
}

export default StaffManagement
